import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UsageEventsApi, GPTEventsApi, PaymentEventsApi } from '../../services/ApiService';

/**
 * CorrelationDetail
 * Shows all events across services linked by a correlation ID.
 * Displays a chronological timeline of events.
 */
export default function CorrelationDetail() {
  const { correlationId } = useParams();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is admin
  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  useEffect(() => {
    if (!correlationId || !isAdmin) return;

    const loadEventsForCorrelation = async () => {
      setLoading(true);
      setError(null);

      try {
        // Search all event types for this correlation ID
        const [usageEventsResp, gptEventsResp, paymentEventsResp] = await Promise.all([
          UsageEventsApi.adminSearch({
            correlationId,
            from: new Date(Date.now() - 365 * 86400000).toISOString(), // Last year
            to: new Date().toISOString(),
            page: 0,
            size: 100
          }).catch(() => null),
          GPTEventsApi.getPromptEvents({
            correlationId,
            from: new Date(Date.now() - 365 * 86400000).toISOString(),
            to: new Date().toISOString(),
            page: 0,
            size: 100
          }).catch(() => null),
          PaymentEventsApi.searchEvents({
            correlationId,
            startDate: new Date(Date.now() - 365 * 86400000).toISOString(),
            endDate: new Date().toISOString(),
            page: 0,
            size: 100
          }).catch(() => null)
        ]);

        // Combine and normalize all events
        const allEvents = [];

        // Usage Events
        if (usageEventsResp?.ok) {
          const usageData = await usageEventsResp.json();
          const usageItems = usageData?.content || usageData?.items || [];
          usageItems.forEach(ev => {
            allEvents.push({
              type: 'usage',
              id: ev.eventId || ev.id,
              timestamp: new Date(ev.occurredAt || ev.createdAt),
              eventType: ev.eventType,
              userId: ev.userId,
              sessionId: ev.sessionId,
              data: ev,
              displayName: `Usage: ${ev.eventType}`,
              color: '#007bff'
            });
          });
        }

        // GPT Prompt Events
        if (gptEventsResp?.ok) {
          const gptData = await gptEventsResp.json();
          const gptItems = gptData?.content || gptData?.items || [];
          gptItems.forEach(ev => {
            allEvents.push({
              type: 'gpt',
              id: ev.eventId || ev.id,
              timestamp: new Date(ev.occurredAt || ev.createdAt),
              eventType: 'GPT_PROMPT',
              userId: ev.userId,
              data: ev,
              displayName: `AI: ${ev.model || 'GPT'} (${(ev.promptTokens || 0) + (ev.completionTokens || 0)} tokens)`,
              color: '#28a745'
            });
          });
        }

        // Payment Events
        if (paymentEventsResp?.ok) {
          const paymentData = await paymentEventsResp.json();
          const paymentItems = paymentData?.content || paymentData?.items || [];
          paymentItems.forEach(ev => {
            allEvents.push({
              type: 'payment',
              id: ev.eventId || ev.id,
              timestamp: new Date(ev.receivedAt || ev.createdAt),
              eventType: ev.eventType,
              userId: ev.userId,
              data: ev,
              displayName: `Payment: ${ev.eventType} (${ev.provider})`,
              color: '#ffc107'
            });
          });
        }

        // Sort by timestamp
        allEvents.sort((a, b) => a.timestamp - b.timestamp);

        setEvents(allEvents);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    loadEventsForCorrelation();
  }, [correlationId, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Correlation Detail</h2>
        <div className="empty-state">Admin access required to view correlation details.</div>
      </div>
    );
  }

  if (!correlationId) {
    return (
      <div className="settings-container">
        <h2>Correlation Detail</h2>
        <div className="empty-state">No correlation ID provided.</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div style={{ marginBottom:'1.5rem' }}>
        <Link to="/settings/analytics-overview" style={{ fontSize:'.9rem', color:'var(--primary)' }}>
          ← Back to Analytics Overview
        </Link>
      </div>

      <h2 style={{ marginBottom:'0.5rem' }}>Correlation Timeline</h2>
      <div style={{ 
        fontSize:'.85rem', 
        color:'#666', 
        marginBottom:'1.5rem',
        padding:'0.75rem 1rem',
        background:'#f8f9fa',
        borderRadius:'8px',
        fontFamily:'monospace'
      }}>
        <strong>Correlation ID:</strong> {correlationId}
      </div>

      {loading && <div>Loading events...</div>}
      {error && <div style={{ color:'#c00' }}>Error loading events: {error.message}</div>}

      {!loading && events.length === 0 && (
        <div className="empty-state">
          No events found for this correlation ID.
          <div style={{ marginTop:'1rem', fontSize:'.85rem', color:'#666' }}>
            Events may have been deleted due to data retention policies, or the correlation ID may be invalid.
          </div>
        </div>
      )}

      {!loading && events.length > 0 && (
        <>
          {/* Summary Stats */}
          <div style={{ 
            display:'grid', 
            gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', 
            gap:'0.75rem', 
            marginBottom:'1.5rem' 
          }}>
            <StatCard label="Total Events" value={events.length.toString()} />
            <StatCard label="UI Events" value={events.filter(e => e.type === 'usage').length.toString()} color="#007bff" />
            <StatCard label="AI Prompts" value={events.filter(e => e.type === 'gpt').length.toString()} color="#28a745" />
            <StatCard label="Payment Events" value={events.filter(e => e.type === 'payment').length.toString()} color="#ffc107" />
            <StatCard label="Duration" value={formatDuration(events[0].timestamp, events[events.length - 1].timestamp)} />
          </div>

          {/* GPT-Specific Stats */}
          {(() => {
            const gptEvents = events.filter(e => e.type === 'gpt');
            if (gptEvents.length === 0) return null;
            
            const totalPromptTokens = gptEvents.reduce((sum, e) => sum + (e.data.promptTokens || 0), 0);
            const totalCompletionTokens = gptEvents.reduce((sum, e) => sum + (e.data.completionTokens || 0), 0);
            const totalTokens = gptEvents.reduce((sum, e) => sum + (e.data.totalTokens || 0), 0);
            const totalCost = gptEvents.reduce((sum, e) => sum + (e.data.costUsd || 0), 0);
            const avgLatency = gptEvents.length > 0 
              ? Math.round(gptEvents.reduce((sum, e) => sum + (e.data.latencyMs || 0), 0) / gptEvents.length)
              : 0;
            
            return (
              <div style={{ 
                marginBottom:'1.5rem',
                padding:'1rem',
                background:'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                borderRadius:'12px',
                color:'white',
                boxShadow:'0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ margin:'0 0 1rem 0', fontSize:'1rem', fontWeight:600 }}>
                  AI Processing Summary
                </h3>
                <div style={{ 
                  display:'grid', 
                  gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', 
                  gap:'0.75rem' 
                }}>
                  <div>
                    <div style={{ fontSize:'0.7rem', opacity:0.9, marginBottom:'0.25rem' }}>Total Calls</div>
                    <div style={{ fontSize:'1.3rem', fontWeight:700 }}>{gptEvents.length}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.7rem', opacity:0.9, marginBottom:'0.25rem' }}>Prompt Tokens</div>
                    <div style={{ fontSize:'1.3rem', fontWeight:700 }}>{totalPromptTokens.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.7rem', opacity:0.9, marginBottom:'0.25rem' }}>Completion Tokens</div>
                    <div style={{ fontSize:'1.3rem', fontWeight:700 }}>{totalCompletionTokens.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.7rem', opacity:0.9, marginBottom:'0.25rem' }}>Total Tokens</div>
                    <div style={{ fontSize:'1.3rem', fontWeight:700 }}>{totalTokens.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.7rem', opacity:0.9, marginBottom:'0.25rem' }}>Total Cost</div>
                    <div style={{ fontSize:'1.3rem', fontWeight:700 }}>${totalCost.toFixed(4)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.7rem', opacity:0.9, marginBottom:'0.25rem' }}>Avg Latency</div>
                    <div style={{ fontSize:'1.3rem', fontWeight:700 }}>{avgLatency}ms</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Timeline Visualization */}
          <div style={{ marginBottom:'1.5rem' }}>
            <h3 style={{ fontSize:'1rem', marginBottom:'1rem' }}>Event Timeline</h3>
            <div style={{ position:'relative' }}>
              {/* Timeline line */}
              <div style={{
                position:'absolute',
                left:'20px',
                top:0,
                bottom:0,
                width:'2px',
                background:'var(--border-default)'
              }} />
              
              {events.map((event, index) => (
                <TimelineEvent 
                  key={event.id} 
                  event={event} 
                  isFirst={index === 0}
                  isLast={index === events.length - 1}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TimelineEvent({ event, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ 
      position:'relative', 
      paddingLeft:'50px', 
      paddingBottom:'1.5rem',
      opacity: isFirst ? 1 : 0.95
    }}>
      {/* Timeline dot */}
      <div style={{
        position:'absolute',
        left:'11px',
        top:'8px',
        width:'20px',
        height:'20px',
        borderRadius:'50%',
        background:'#fff',
        border:`3px solid ${event.color}`,
        zIndex:1
      }} />
      
      {/* Event card */}
      <div style={{
        background:'#fff',
        border:'1px solid var(--border-light)',
        borderLeft:`4px solid ${event.color}`,
        borderRadius:'8px',
        padding:'1rem',
        boxShadow:'var(--shadow-sm)',
        cursor:'pointer',
        transition:'all 0.2s'
      }}
      onClick={() => setExpanded(!expanded)}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
      >
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
          <div>
            <div style={{ fontSize:'.95rem', fontWeight:600, color:event.color }}>
              {event.displayName}
            </div>
            <div style={{ fontSize:'.75rem', color:'#666', marginTop:'0.25rem' }}>
              {event.timestamp.toLocaleString()}
            </div>
          </div>
          <div style={{ fontSize:'1.2rem', color:'#ccc' }}>
            {expanded ? '▼' : '▶'}
          </div>
        </div>
        
        {/* Basic info always visible */}
        <div style={{ fontSize:'.8rem', color:'#666', display:'flex', flexWrap:'wrap', gap:'1rem' }}>
          {event.userId && <span><strong>User:</strong> {event.userId}</span>}
          {event.sessionId && <span><strong>Session:</strong> {event.sessionId.slice(0, 8)}...</span>}
        </div>

        {/* Expanded details */}
        {expanded && (
          <div style={{ 
            marginTop:'1rem', 
            paddingTop:'1rem', 
            borderTop:'1px solid var(--border-light)',
            fontSize:'.8rem'
          }}>
            <pre style={{
              background:'var(--color-bg-subtle)',
              padding:'0.75rem',
              borderRadius:'6px',
              overflow:'auto',
              maxHeight:'300px',
              fontSize:'.75rem'
            }}>
              {JSON.stringify(event.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color = '#333' }) {
  return (
    <div style={{
      background:'#fff',
      border:'1px solid var(--border-light)',
      borderRadius:'8px',
      padding:'0.75rem',
      textAlign:'center'
    }}>
      <div style={{ fontSize:'.7rem', color:'#666', marginBottom:'0.25rem', textTransform:'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize:'1.5rem', fontWeight:700, color }}>
        {value}
      </div>
    </div>
  );
}

function formatDuration(start, end) {
  const diffMs = end - start;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  
  if (diffHour > 0) return `${diffHour}h ${diffMin % 60}m`;
  if (diffMin > 0) return `${diffMin}m ${diffSec % 60}s`;
  return `${diffSec}s`;
}
